import React, { useEffect, useRef, useState } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  TextField,
  MenuItem,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import RideApi from "Services/Ride";
import PlaceApi from "Services/Place";
import VehicleApi from "Services/Vehicle";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const RideCreateModal = ({ openDialog, handleClose, rideId = "" }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [places, setPlaces] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const isEditMode = Boolean(rideId);
  const lastLoadedRideIdRef = useRef("");

  const emptyWaypoint = {
    waypoint_place_id: "",
    waypoint_sequence_order: 0,
    waypoint_arrival_offset_min: 0,
    waypoint_price_override: 0,
  };

  const normalizePlaceList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.data?.data)) return payload?.data?.data || [];
    if (Array.isArray(payload?.data)) return payload?.data || [];
    return [];
  };

  const getPlaceLabel = (place) => {
    const name = place?.name || place?.id || place?._id || "Unknown";
    const locality = place?.addressLocality ? ` - ${place.addressLocality}` : "";
    const country = place?.addressCountry ? ` (${place.addressCountry})` : "";
    return `${name}${locality}${country}`;
  };

  const normalizeVehicleList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.data?.data)) return payload?.data?.data || [];
    if (Array.isArray(payload?.data)) return payload?.data || [];
    return [];
  };

  const getVehicleLabel = (vehicle) => {
    const plate = vehicle?.vehicle_name || vehicle?.vehicle_no;
    return plate;
  };

  const toDateTimeLocal = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  useEffect(() => {
    if (!openDialog) return;
    PlaceApi.getPlaceList({ page: 1, limit: "unlimited", search: "" }).then(
      (res) => setPlaces(normalizePlaceList(res)),
      () => {
        setPlaces([]);
        dispatch(
          showSnackbar({
            message: "Unable to load places",
            autoHideDuration: 2500,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
    VehicleApi.getVehicleList({ page: 1, limit: "unlimited", search: "" }).then(
      (res) => setVehicles(normalizeVehicleList(res)),
      () => setVehicles([])
    );
  }, [dispatch, openDialog]);

  const formik = useFormik({
    initialValues: {
      origin_place_id: "",
      destination_place_id: "",
      departure_at: "",
      estimated_arrival_at: "",
      total_seats: 1,
      price_per_seat: 0,
      currency: "XAF",
      comfort_class: "standard",
      status: "draft",
      vehicle_id: "",
      notes: "",
      is_recurring: false,
      recurrence_rule: "",
      route_polyline: "",
      waypoints: [{ ...emptyWaypoint }],
    },
    validationSchema: yup.object({
      origin_place_id: yup.string().required("Origin place id is required"),
      destination_place_id: yup.string().required("Destination place id is required"),
      departure_at: yup.string().required("Departure time is required"),
      estimated_arrival_at: yup.string().required("Estimated arrival is required"),
      total_seats: yup.number().required("Total seats is required").min(1, "Minimum 1"),
      price_per_seat: yup.number().required("Price per seat is required").min(0, "Minimum 0"),
      currency: yup.string().required("Currency is required"),
      comfort_class: yup.string().required("Comfort class is required"),
    }),
    onSubmit: (values) => {
      dispatch(showLoader("Loading please wait..."));
      const payload = {
        origin_place_id: values?.origin_place_id,
        destination_place_id: values?.destination_place_id,
        departure_at: new Date(values?.departure_at).toISOString(),
        estimated_arrival_at: new Date(values?.estimated_arrival_at).toISOString(),
        total_seats: Number(values?.total_seats),
        price_per_seat: Number(values?.price_per_seat),
        currency: values?.currency,
        comfort_class: values?.comfort_class,
        status: values?.status,
        vehicle_id: values?.vehicle_id || null,
        notes: values?.notes || "",
        is_recurring: Boolean(values?.is_recurring),
        recurrence_rule: values?.recurrence_rule || "",
        route_polyline: values?.route_polyline || "",
        waypoints: (values?.waypoints || [])
          .filter((wp) => Boolean(wp?.waypoint_place_id))
          .map((wp) => ({
            place_id: wp?.waypoint_place_id,
            sequence_order: Number(wp?.waypoint_sequence_order || 0),
            arrival_offset_min: Number(wp?.waypoint_arrival_offset_min || 0),
            price_override: Number(wp?.waypoint_price_override || 0),
          })),
      };

      const request = isEditMode ? RideApi.updateRide(rideId, payload) : RideApi.createRide(payload);
      request.then(
        (res) => {
          dispatch(hideLoader());
          if (res?.status === 200 || res?.status === 201) {
            dispatch(
              showSnackbar({
                message: res?.data?.message || (isEditMode ? "Ride updated successfully" : "Ride created successfully"),
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "success",
              })
            );
            formik.resetForm();
            handleClose(true);
          }
        },
        (err) => {
          dispatch(hideLoader());
          dispatch(
            showSnackbar({
              message: err?.response?.data?.message || (isEditMode ? "Error while updating ride" : "Error while creating ride"),
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "error",
            })
          );
        }
      );
    },
  });

  useEffect(() => {
    if (!openDialog || !isEditMode || !rideId) return;
    if (lastLoadedRideIdRef.current === rideId) return;
    lastLoadedRideIdRef.current = rideId;
    dispatch(showLoader("Loading ride details..."));
    RideApi.getRideById(rideId).then(
      (res) => {
        dispatch(hideLoader());
        const data = res?.data || {};
        const rideWaypoints = Array.isArray(data?.ride_waypoints) ? data.ride_waypoints : [];
        formik.setValues({
          origin_place_id: data?.origin_place_id || "",
          destination_place_id: data?.destination_place_id || "",
          departure_at: toDateTimeLocal(data?.departure_at),
          estimated_arrival_at: toDateTimeLocal(data?.estimated_arrival_at),
          total_seats: data?.total_seats ?? 1,
          price_per_seat: Number(data?.price_per_seat || 0),
          currency: data?.currency || "XAF",
          comfort_class: data?.comfort_class || "standard",
          status: data?.status || "draft",
          vehicle_id: data?.vehicle_id || "",
          notes: data?.notes || "",
          is_recurring: Boolean(data?.is_recurring),
          recurrence_rule: data?.recurrence_rule || "",
          route_polyline: data?.route_polyline || "",
          waypoints: rideWaypoints.length
            ? rideWaypoints.map((wp) => ({
                waypoint_place_id: wp?.place_id || "",
                waypoint_sequence_order: Number(wp?.sequence_order || 0),
                waypoint_arrival_offset_min: Number(wp?.arrival_offset_min || 0),
                waypoint_price_override: Number(wp?.price_override || 0),
              }))
            : [{ ...emptyWaypoint }],
        });
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Unable to load ride details",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
        lastLoadedRideIdRef.current = "";
      }
    );
  }, [dispatch, isEditMode, openDialog, rideId]);

  useEffect(() => {
    if (!openDialog) {
      lastLoadedRideIdRef.current = "";
      return;
    }
    if (!isEditMode) formik.resetForm();
  }, [isEditMode, openDialog]);

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => handleClose(false)}
      maxWidth="xl"
      aria-describedby="ride-dialog"
      className={classes.root}
    >
      <DialogTitle>
        <section className={classes.dialogTitleContainer}>
          <Typography variant="body1" className={classes.title}>
            {isEditMode ? "Edit Ride" : "Add Ride"}
          </Typography>
          <IconButton aria-label="close" onClick={() => handleClose(false)}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </section>
      </DialogTitle>
      <DialogContent>
        <Grid container className={classes.containerWrapper}>
          <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" select label="Origin Place" name="origin_place_id" value={formik.values.origin_place_id} onChange={formik.handleChange} error={formik.touched.origin_place_id && Boolean(formik.errors.origin_place_id)} helperText={formik.touched.origin_place_id && formik.errors.origin_place_id}>
              {places.map((place) => {
                const placeId = place?.id || place?._id;
                if (!placeId) return null;
                return (
                  <MenuItem key={placeId} value={placeId}>
                    {getPlaceLabel(place)}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" select label="Destination Place" name="destination_place_id" value={formik.values.destination_place_id} onChange={formik.handleChange} error={formik.touched.destination_place_id && Boolean(formik.errors.destination_place_id)} helperText={formik.touched.destination_place_id && formik.errors.destination_place_id}>
              {places.map((place) => {
                const placeId = place?.id || place?._id;
                if (!placeId) return null;
                return (
                  <MenuItem key={placeId} value={placeId}>
                    {getPlaceLabel(place)}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" type="datetime-local" label="Departure At" name="departure_at" InputLabelProps={{ shrink: true }} value={formik.values.departure_at} onChange={formik.handleChange} error={formik.touched.departure_at && Boolean(formik.errors.departure_at)} helperText={formik.touched.departure_at && formik.errors.departure_at} />
          </Grid>
          <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" type="datetime-local" label="Estimated Arrival At" name="estimated_arrival_at" InputLabelProps={{ shrink: true }} value={formik.values.estimated_arrival_at} onChange={formik.handleChange} error={formik.touched.estimated_arrival_at && Boolean(formik.errors.estimated_arrival_at)} helperText={formik.touched.estimated_arrival_at && formik.errors.estimated_arrival_at} />
          </Grid>
          <Grid item xs={12} md={3} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" type="number" label="Total Seats" name="total_seats" value={formik.values.total_seats} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} md={3} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" type="number" label="Price Per Seat" name="price_per_seat" value={formik.values.price_per_seat} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} md={3} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" label="Currency" name="currency" value={formik.values.currency} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} md={3} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" select label="Comfort Class" name="comfort_class" value={formik.values.comfort_class} onChange={formik.handleChange}>
              <MenuItem value="standard">standard</MenuItem>
              <MenuItem value="comfort">comfort</MenuItem>
              <MenuItem value="premium">premium</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" select label="Status" name="status" value={formik.values.status} onChange={formik.handleChange}>
              <MenuItem value="draft">draft</MenuItem>
              <MenuItem value="published">published</MenuItem>
              <MenuItem value="full">full</MenuItem>
              <MenuItem value="cancelled">cancelled</MenuItem>
              <MenuItem value="completed">completed</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" select label="Vehicle (optional)" name="vehicle_id" value={formik.values.vehicle_id} onChange={formik.handleChange}>
              <MenuItem value="">None</MenuItem>
              {vehicles.map((vehicle) => {
                const vehicleId = vehicle?.id || vehicle?._id;
                if (!vehicleId) return null;
                return (
                  <MenuItem key={vehicleId} value={vehicleId}>
                    {getVehicleLabel(vehicle)}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" select label="Recurring" name="is_recurring" value={String(formik.values.is_recurring)} onChange={(e) => formik.setFieldValue("is_recurring", e.target.value === "true")}>
              <MenuItem value="false">false</MenuItem>
              <MenuItem value="true">true</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" label="Recurrence Rule (optional)" name="recurrence_rule" value={formik.values.recurrence_rule} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" label="Route Polyline (optional)" name="route_polyline" value={formik.values.route_polyline} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} className={classes.textFieldWrapper}>
            <TextField fullWidth size="small" multiline rows={3} label="Notes (optional)" name="notes" value={formik.values.notes} onChange={formik.handleChange} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">Waypoint (optional)</Typography>
          </Grid>
          {(formik.values.waypoints || []).map((waypoint, index) => (
            <React.Fragment key={`waypoint-${index}`}>
              <Grid item xs={12} md={5} className={classes.textFieldWrapper}>
                <TextField fullWidth size="small" select label={`Waypoint Place ${index + 1}`} name={`waypoints.${index}.waypoint_place_id`} value={waypoint.waypoint_place_id} onChange={formik.handleChange}>
                  <MenuItem value="">None</MenuItem>
                  {places.map((place) => {
                    const placeId = place?.id || place?._id;
                    if (!placeId) return null;
                    return (
                      <MenuItem key={placeId} value={placeId}>
                        {getPlaceLabel(place)}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2} className={classes.textFieldWrapper}>
                <TextField fullWidth size="small" type="number" label="Sequence" name={`waypoints.${index}.waypoint_sequence_order`} value={waypoint.waypoint_sequence_order} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} md={2} className={classes.textFieldWrapper}>
                <TextField fullWidth size="small" type="number" label="Offset (min)" name={`waypoints.${index}.waypoint_arrival_offset_min`} value={waypoint.waypoint_arrival_offset_min} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} md={2} className={classes.textFieldWrapper}>
                <TextField fullWidth size="small" type="number" label="Price Override" name={`waypoints.${index}.waypoint_price_override`} value={waypoint.waypoint_price_override} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} md={1} className={classes.textFieldWrapper}>
                <Button
                  color="error"
                  onClick={() => {
                    const next = [...(formik.values.waypoints || [])];
                    next.splice(index, 1);
                    formik.setFieldValue("waypoints", next.length ? next : [{ ...emptyWaypoint }]);
                  }}
                  startIcon={<DeleteOutlineIcon />}
                >
                  Remove
                </Button>
              </Grid>
            </React.Fragment>
          ))}
          <Grid item xs={12} className={classes.textFieldWrapper}>
            <Stack direction="row" justifyContent="flex-start">
              <Button
                variant="outlined"
                startIcon={<AddOutlinedIcon />}
                onClick={() => {
                  formik.setFieldValue("waypoints", [...(formik.values.waypoints || []), { ...emptyWaypoint }]);
                }}
              >
                Add waypoint
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogActions}>
        <section className={classes.closeBtn}>
          <Button onClick={() => handleClose(false)}>Close</Button>
        </section>
        <Button variant="contained" onClick={formik.handleSubmit}>
          {isEditMode ? "Update Ride" : "Add Ride"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RideCreateModal;
